import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { AlertCircle, Sparkles, Lock } from 'lucide-react';
import type { WbBusinessInfo } from '@/lib/api/services/websiteBuilder';

export const FIELD_LIMITS = {
  business_name: 100,
  services: 2000,
  support_phone: 50,
  support_email: 200,
  instagram_handle: 100,
  website: 500,
} as const;

export const MIN_BUSINESS_NAME_LENGTH = 10;
export const MIN_SERVICES_LENGTH = 10;

export const BUSINESS_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Cafe / Bakery' },
  { value: 'retail', label: 'Retail / D2C' },
  { value: 'salon', label: 'Salon / Spa' },
  { value: 'gym', label: 'Gym / Fitness' },
  { value: 'coaching', label: 'Coaching / Tutoring' },
  { value: 'clinic', label: 'Clinic / Healthcare' },
  { value: 'services', label: 'Professional Services' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'creator', label: 'Creator / Influencer' },
  { value: 'other', label: 'Other' },
];

const BUSINESS_TYPE_VALUES = new Set(BUSINESS_TYPE_OPTIONS.map((o) => o.value));

interface MiniFormProps {
  initialValues: WbBusinessInfo;
  lockedFields: Array<keyof WbBusinessInfo>;
  isAdmin: boolean;
  onSubmit: (values: WbBusinessInfo) => void;
  onSkip?: () => void;
}

const trim = (v: string | undefined | null) => (typeof v === 'string' ? v.trim() : '');

const isUrl = (v: string) => /^https?:\/\/\S+/i.test(v);
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

function validate(state: WbBusinessInfo): Record<string, string> {
  const errors: Record<string, string> = {};

  const businessName = trim(state.business_name);
  if (!businessName) {
    errors.business_name = 'Business name is required';
  } else if (businessName.length > FIELD_LIMITS.business_name) {
    errors.business_name = `Too long (max ${FIELD_LIMITS.business_name} chars)`;
  } else if (businessName.length < MIN_BUSINESS_NAME_LENGTH) {
    errors.business_name = `At least ${MIN_BUSINESS_NAME_LENGTH} characters`;
  }

  const businessType = trim(state.business_type);
  if (!businessType) {
    errors.business_type = 'Pick the category that fits';
  } else if (!BUSINESS_TYPE_VALUES.has(businessType)) {
    errors.business_type = 'Pick one of the listed categories';
  }

  const services = trim(state.services);
  if (!services) {
    errors.services = 'Tell us what you offer — required';
  } else if (services.length < MIN_SERVICES_LENGTH) {
    errors.services = `At least ${MIN_SERVICES_LENGTH} characters`;
  } else if (services.length > FIELD_LIMITS.services) {
    errors.services = `Too long (max ${FIELD_LIMITS.services} chars)`;
  }

  const phone = trim(state.support_phone);
  if (phone && phone.length > FIELD_LIMITS.support_phone) {
    errors.support_phone = `Too long (max ${FIELD_LIMITS.support_phone} chars)`;
  }

  const email = trim(state.support_email);
  if (email) {
    if (!isEmail(email)) errors.support_email = 'Must be a valid email';
    else if (email.length > FIELD_LIMITS.support_email) {
      errors.support_email = `Too long (max ${FIELD_LIMITS.support_email} chars)`;
    }
  }

  const handle = trim(state.instagram_handle).replace(/^@+/, '');
  if (handle && handle.length > FIELD_LIMITS.instagram_handle) {
    errors.instagram_handle = `Too long (max ${FIELD_LIMITS.instagram_handle} chars)`;
  }

  const website = trim(state.website);
  if (website) {
    if (!isUrl(website)) errors.website = 'Must be a valid http(s) URL';
    else if (website.length > FIELD_LIMITS.website) {
      errors.website = `Too long (max ${FIELD_LIMITS.website} chars)`;
    }
  }

  return errors;
}

export const MiniForm: React.FC<MiniFormProps> = ({
  initialValues,
  lockedFields,
  isAdmin,
  onSubmit,
}) => {
  const [state, setState] = useState<WbBusinessInfo>({
    business_name: initialValues.business_name ?? '',
    business_type: initialValues.business_type ?? '',
    services: initialValues.services ?? '',
    support_phone: initialValues.support_phone ?? '',
    support_email: initialValues.support_email ?? '',
    instagram_handle: initialValues.instagram_handle ?? '',
    website: initialValues.website ?? '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => validate(state), [state]);

  const isLocked = (key: keyof WbBusinessInfo) =>
    lockedFields.includes(key);

  const update = (key: keyof WbBusinessInfo, value: string) => {
    setState((s) => ({ ...s, [key]: value }));
  };

  const markTouched = (key: string) => {
    setTouched((t) => (t[key] ? t : { ...t, [key]: true }));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isAdmin) return;
    const validation = validate(state);
    if (Object.keys(validation).length > 0) {
      setTouched({
        business_name: true,
        business_type: true,
        services: true,
        support_phone: true,
        support_email: true,
        instagram_handle: true,
        website: true,
      });
      return;
    }
    onSubmit({
      ...state,
      instagram_handle: trim(state.instagram_handle).replace(/^@+/, ''),
    });
  };

  const showError = (key: keyof WbBusinessInfo) =>
    (touched[key] || touched._all) ? errors[key] || null : null;

  const hasAnyError = Object.keys(errors).length > 0;
  const submitDisabled = hasAnyError;

  const lockedHint = (
    <span className="text-[10px] text-gray-500 inline-flex items-center gap-1">
      <Lock className="h-3 w-3" />
      managed in Settings → AI Knowledge
    </span>
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-md border border-gray-200 p-4 bg-white"
      data-testid="wb-mini-form"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-purple-600" />
        <h3 className="text-sm font-semibold text-gray-900">What's your business?</h3>
      </div>
      <p className="text-xs text-gray-600 -mt-1">
        We use this to personalize your template. You can edit any of it later.
      </p>

      {!isAdmin && (
        <div
          className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2"
          data-testid="wb-mini-non-admin-banner"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Ask your admin to fill out AI Knowledge before using templates.
            Only organization admins can edit this context.
          </div>
        </div>
      )}

      <FormField
        label="Business name"
        required
        error={showError('business_name')}
        limit={FIELD_LIMITS.business_name}
        value={state.business_name ?? ''}
        locked={isLocked('business_name')}
      >
        <Input
          value={state.business_name ?? ''}
          onChange={(e) => update('business_name', e.target.value)}
          onBlur={() => markTouched('business_name')}
          disabled={!isAdmin || isLocked('business_name')}
          maxLength={FIELD_LIMITS.business_name + 10}
          placeholder="e.g. Hussain's Diner"
          data-testid="wb-mini-business-name"
        />
      </FormField>

      <FormField
        label="Business type"
        required
        error={showError('business_type')}
        locked={isLocked('business_type')}
      >
        <select
          value={state.business_type ?? ''}
          onChange={(e) => update('business_type', e.target.value)}
          onBlur={() => markTouched('business_type')}
          disabled={!isAdmin || isLocked('business_type')}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          data-testid="wb-mini-business-type"
        >
          <option value="">Pick a category…</option>
          {BUSINESS_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        label="What you offer"
        required
        error={showError('services')}
        limit={FIELD_LIMITS.services}
        value={state.services ?? ''}
        helper="Helps the AI write copy that fits your business."
        locked={isLocked('services')}
      >
        <Textarea
          value={state.services ?? ''}
          onChange={(e) => update('services', e.target.value)}
          onBlur={() => markTouched('services')}
          disabled={!isAdmin || isLocked('services')}
          maxLength={FIELD_LIMITS.services + 50}
          rows={4}
          placeholder="We serve authentic Mughlai cuisine, weekday lunch specials, and family dinner platters."
          data-testid="wb-mini-services"
        />
      </FormField>

      <FormField
        label="WhatsApp / phone"
        optional
        error={showError('support_phone')}
        limit={FIELD_LIMITS.support_phone}
        value={state.support_phone ?? ''}
        locked={isLocked('support_phone')}
      >
        <Input
          value={state.support_phone ?? ''}
          onChange={(e) => update('support_phone', e.target.value)}
          onBlur={() => markTouched('support_phone')}
          disabled={!isAdmin || isLocked('support_phone')}
          maxLength={FIELD_LIMITS.support_phone + 10}
          placeholder="+91 98765 43210"
          data-testid="wb-mini-support-phone"
        />
      </FormField>

      {!isLocked('support_email') && (
        <FormField
          label="Support email"
          optional
          error={showError('support_email')}
          limit={FIELD_LIMITS.support_email}
          value={state.support_email ?? ''}
          locked={false}
        >
          <Input
            type="email"
            value={state.support_email ?? ''}
            onChange={(e) => update('support_email', e.target.value)}
            onBlur={() => markTouched('support_email')}
            disabled={!isAdmin}
            maxLength={FIELD_LIMITS.support_email + 10}
            placeholder="hello@example.com"
            data-testid="wb-mini-support-email"
          />
        </FormField>
      )}

      {!isLocked('instagram_handle') && (
        <FormField
          label="Instagram handle"
          optional
          error={showError('instagram_handle')}
          limit={FIELD_LIMITS.instagram_handle}
          value={(state.instagram_handle ?? '').replace(/^@+/, '')}
          locked={false}
        >
          <Input
            value={state.instagram_handle ?? ''}
            onChange={(e) => update('instagram_handle', e.target.value)}
            onBlur={() => markTouched('instagram_handle')}
            disabled={!isAdmin}
            maxLength={FIELD_LIMITS.instagram_handle + 10}
            placeholder="@hussainsdiner"
            data-testid="wb-mini-instagram-handle"
          />
        </FormField>
      )}

      {!isLocked('website') && (
        <FormField
          label="Website"
          optional
          error={showError('website')}
          limit={FIELD_LIMITS.website}
          value={state.website ?? ''}
          locked={false}
        >
          <Input
            value={state.website ?? ''}
            onChange={(e) => update('website', e.target.value)}
            onBlur={() => markTouched('website')}
            disabled={!isAdmin}
            maxLength={FIELD_LIMITS.website + 10}
            placeholder="https://example.com"
            data-testid="wb-mini-website"
          />
        </FormField>
      )}

      {isAdmin && (
        <div className="flex justify-end pt-1">
          <Button
            type="submit"
            disabled={submitDisabled}
            data-testid="wb-mini-submit"
          >
            Save &amp; continue
          </Button>
        </div>
      )}
    </form>
  );
};

interface FieldProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: string | null;
  limit?: number;
  value?: string;
  helper?: string;
  locked?: boolean;
  children: React.ReactNode;
}

const FormField: React.FC<FieldProps> = ({
  label,
  required,
  optional,
  error,
  limit,
  value,
  helper,
  locked,
  children,
}) => {
  const length = (value || '').length;
  return (
    <div>
      <Label className="text-xs flex items-center justify-between mb-1">
        <span className="flex items-center gap-2">
          <span>
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
            {optional && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
          </span>
          {locked && (
            <span className="text-[10px] text-gray-500 inline-flex items-center gap-1">
              <Lock className="h-3 w-3" />
              managed in Settings → AI Knowledge
            </span>
          )}
        </span>
        {limit ? (
          <span
            className={`text-[10px] tabular-nums ${
              length > limit ? 'text-red-500' : 'text-gray-400'
            }`}
          >
            {length}/{limit}
          </span>
        ) : null}
      </Label>
      {children}
      {error && (
        <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {error}
        </p>
      )}
      {!error && helper && (
        <p className="text-[11px] text-gray-500 mt-1">{helper}</p>
      )}
    </div>
  );
};
